<template>
  <div class="[ drawer-activity-types ]">

    <h1 class="[ drawer-header ]">
      <template v-if="!dbReady">Loading activity types...</template>
      <template v-else>
        Activity Types
        <span class="text-xs ml-2 text-pink-300 tracking-wider">TOTAL: {{totalNumberOfActivityTypes}}</span>
      </template>
    </h1>

    <EditDeleteModeButtons
      bodyClassPrefix="activity-types"
      v-model:editModeActive="allowEditingItems"
      v-model:deleteModeActive="allowDeletingItems"
      @update:editModeActive="onEditModeChange"
      @update:deleteModeActive="onDeleteModeChange"
    />

    <div class="[ list-of-entries-wrapper ]" ref="listOfActivityTypes" v-if="allActivityTypes">

      <div class="[ list-of-entries-header ]">
        <span class="font-bold">Select Activity type</span>
        <span class="text-sm text-gray-200">Define what you are doing.</span>
      </div>

      <div class="[ list-of-entries ]" v-if="allActivityTypes">

        <div
          class="[ list-entry ] items-center"
          v-for="item in allActivityTypes"
          :key="item.id"
          :data-id="item.id">

          <label
            class="[ list-entry-label ] w-full"
            :class="{ 'is-active': currentActivityType === item.id }"
          >
            <input type="radio" :value="item.id" v-model="currentActivityType" class="sr-only">
            <!-- Color swatch (clickable to edit) -->
            <span
              class="inline-block w-4 h-4 rounded-full shrink-0 border border-white/30 cursor-pointer hover:scale-125 transition-transform"
              :style="{ backgroundColor: getHexForItem(item) }"
              :title="'Click to edit activity type'"
              @click.stop="editOneItem(item)"
            ></span>
            <!-- Activity icon -->
            <Icon
              v-if="item.iconName"
              :icon="item.iconName"
              class="w-5 h-5 ml-2 shrink-0 text-pink-300"
            />
            <span class="ml-2">{{ item.title }}</span>
          </label>

          <div
            class="[ edit-delete-icons-wrapper ]"
            :class="{ 'row-actions-visible': showRowActions }"
          >
            <svg @click="openModal(item.id)" class="delete-icon">
              <use xlink:href="#cross"/>
            </svg>
            <svg @click="editOneItem(item)" class="edit-icon">
              <use xlink:href="#notes"/>
            </svg>
          </div>

        </div>

      </div>

        <!-- Check-uncheck all -->
        <div class="[ check-uncheck-wrapper ] justify-between">
          <div class="uncheck" @click="uncheckList()" v-if="totalNumberOfActivityTypes > 0">Uncheck All</div>
          <div class="add-presets" @click="reAddInitialData()" v-else>Add presets</div>
        </div>

        <!-- Re-add presets -->
        <div class="add-presets-msg" v-html="reAddMessage" v-if="reAddingInitialDataSuccessful"></div>

    </div>

    <div class="mt-auto w-full border-t border-pink-200/20 px-3 py-3">
      <div v-if="allowDeletingItems" class="[ editor-edit-bar ] flex h-full items-center justify-center w-full p-3 text-amber-400 font-bold">
        Deactivate Delete Mode
      </div>
      <div v-else-if="allowEditingItems" class="[ editor-edit-bar ] flex h-full items-center justify-center w-full p-3 text-amber-400 font-bold">
        Deactivate Edit Mode
      </div>
      <div v-else class="flex flex-row justify-center gap-3">
        <button type="button" class="btn btn-primary text-sm" @click="openCreateModal">
          Add Activity Type
        </button>
      </div>
    </div>

    <ActivityTypeEditorModal
      v-model="activityTypeModalVisible"
      :mode="activityTypeModalMode"
      :initialData="activityTypeInitialData"
      :colorPalette="colorPalette"
      :usedColorIds="usedColorIds"
      :iconOptions="enabledIconOptions"
      :saving="activityTypeSaving"
      :errorMessage="activityTypeError"
      @submit="submitActivityTypeEditor"
    />

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import { useDeleteConfirmStore, useSettingsStore, useActivityTypesStore, useColorPalettesStore, useIconCatalogStore, useUiStore } from '@/stores';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';
import { Icon } from '@iconify/vue';
import ActivityTypeEditorModal from '@/components/modal/ActivityTypeEditorModal.vue';

defineOptions({ name: 'ActivityTypes' });

const EditDeleteModeButtons = defineAsyncComponent(() => import('@/components/ui/EditDeleteModeButtons.vue'));

const settingsStore = useSettingsStore();
const activityTypesStore = useActivityTypesStore();
const colorPalettesStore = useColorPalettesStore();
const iconCatalogStore = useIconCatalogStore();
const uiStore = useUiStore();
const deleteConfirmStore = useDeleteConfirmStore();

const id = ref(null);
const createdAt = ref(null);
const feedback = ref(null);

// Local Attributes
const allowDeletingItems = ref(false);
const allowEditingItems = ref(false);
const idOfItemToDelete = ref(null);
const reAddMessage = ref(null);
const reAddingInitialDataSuccessful = ref(false);

const selectedColorId = ref(20); // Default to color ID 20 (Pink)
const activityTypeModalVisible = ref(false);
const activityTypeModalMode = ref('edit');
const activityTypeInitialData = ref({});
const activityTypeSaving = ref(false);
const activityTypeError = ref('');


// --- COMPUTED ---
const dbReady = computed(() => settingsStore.dbReady);
const allActivityTypes = computed(() => activityTypesStore.activityTypes);
const totalNumberOfActivityTypes = computed(() => allActivityTypes.value?.length || 0);
const activeMenu = computed(() => uiStore.activeMenu);
const mainNavStatus = computed(() => uiStore.mainNavStatus);
const showRowActions = computed(() => allowEditingItems.value || allowDeletingItems.value);

// Color palette from new store
const colorPalette = computed(() => colorPalettesStore.getActiveColors);
const usedColorIds = computed(() => activityTypesStore.getUsedColorIds);
const enabledIconOptions = computed(() => iconCatalogStore.getEnabledIconEntries || []);

const currentActivityType = computed({
  get: () => activityTypesStore.currentActivityType,
  set: (val) => {
    activityTypesStore.setCurrentActivityType(val);
    setCurrentActivityTypeObj();
  }
});

// --- METHODS ---
function onEditModeChange (isActive) {
  if (!isActive) {
    activityTypeModalVisible.value = false;
  }
}

function onDeleteModeChange (isActive) {
  if (isActive) {
    activityTypeModalVisible.value = false;
  }
}

function closeAllModes () {
  allowEditingItems.value = false;
  allowDeletingItems.value = false;
  activityTypeModalVisible.value = false;
  idOfItemToDelete.value = null;
  resetProps();

  document.body.classList.remove('activity-types-edit');
  document.body.classList.remove('activity-types-delete');
}

function openCreateModal () {
  activityTypeModalMode.value = 'create';
  id.value = null;
  createdAt.value = null;
  activityTypeError.value = '';
  activityTypeInitialData.value = {
    title: '',
    colorId: 20,
    iconName: enabledIconOptions.value[0]?.iconKey || 'solar:walking-linear'
  };
  activityTypeModalVisible.value = true;
}

function editOneItem (item) {
  activityTypeModalMode.value = 'edit';
  id.value = item.id;
  createdAt.value = item.createdAt || null;
  const itemColorId = item.colorId || 20;
  selectedColorId.value = itemColorId;
  activityTypeError.value = '';
  activityTypeInitialData.value = {
    title: item.title,
    colorId: itemColorId,
    iconName: item.iconName || enabledIconOptions.value[0]?.iconKey || 'solar:walking-linear'
  };
  activityTypeModalVisible.value = true;
}

async function addItem (obj) {
  const newItemObj = {
    title: obj.title,
    colorId: obj.colorId || selectedColorId.value, // Include selected color ID
    iconName: obj.iconName || enabledIconOptions.value[0]?.iconKey || 'solar:walking-linear',
    createdAt: Date.now()
  };

  try {
    const newActivityId = await GeoMoverDB.addOne('activityTypes', newItemObj);
    feedback.value = 'Saved.';
    getAllActivityTypes(newActivityId);
    return true;
  }
  catch (error) {
    appDebugLog.error('ActivityTypes', 'addItem error:', error);
    feedback.value = 'Error.';
    return false;
  }
  finally {
    clearFeedbackTimeout();
    resetProps();
  }
}

async function updateItem (obj) {
  if (obj.id === null) {
    feedback.value = 'No activity selected to update.';
    return false;
  }

  const editedItemObj = {
    id: obj.id,
    title: obj.title,
    colorId: obj.colorId || selectedColorId.value, // Include selected color ID
    iconName: obj.iconName || enabledIconOptions.value[0]?.iconKey || 'solar:walking-linear',
    createdAt: obj.createdAt || Date.now(),
    updated: Date.now()
  };

  try {
    await GeoMoverDB.updateOne('activityTypes', editedItemObj, obj.id);
    feedback.value = 'Update successful.';
    getAllActivityTypes();
    return true;
  }
  catch (error) {
    appDebugLog.error('ActivityTypes', 'updateItem error:', error);
    feedback.value = 'Update error.';
    return false;
  }
  finally {
    clearFeedbackTimeout();
    resetProps();
  }
}

async function submitActivityTypeEditor (payload) {
  activityTypeSaving.value = true;
  activityTypeError.value = '';

  try {
    if (activityTypeModalMode.value === 'create') {
      const didSave = await addItem(payload);
      if (didSave) {
        activityTypeModalVisible.value = false;
        return;
      }
      activityTypeError.value = feedback.value || 'Could not save activity type.';
      return;
    }

    const didSave = await updateItem({
      ...payload,
      id: id.value,
      createdAt: createdAt.value
    });

    if (didSave) {
      activityTypeModalVisible.value = false;
      return;
    }

    activityTypeError.value = feedback.value || 'Could not save activity type changes.';
  }
  catch (error) {
    activityTypeError.value = error?.message || 'Could not save activity type.';
  }
  finally {
    activityTypeSaving.value = false;
  }
}

async function deleteItem () {
  try {
    await GeoMoverDB.deleteOne('activityTypes', idOfItemToDelete.value);
    getAllActivityTypes();
  }
  catch (error) {
    appDebugLog.error('ActivityTypes', 'deleteItem error:', error);
  }
}

async function getAllActivityTypes (idOfChangedItem = null) {
  try {
    const result = await GeoMoverDB.findAll('activityTypes');
    activityTypesStore.loadActivityTypes(result);
    setCurrentActivityTypeObj();
  }
  catch (error) {
    activityTypesStore.loadActivityTypes({ id: 0, title: 'Error loading activity types! Error = ' + error });
  }

  if (idOfChangedItem !== null) {
    indicateChange(idOfChangedItem);
  }
}

function indicateChange (itemId = null) {
  if (itemId != null) {
    const changedItem = document.querySelector('[data-id="' + itemId + '"]');
    if (changedItem) {
      changedItem.classList.add('item-changed');
      setTimeout(() => {
        changedItem.classList.remove('item-changed');
      }, 2000);
    }
  }
}

function clearReAddMsgField (dur) {
  setTimeout(() => {
    reAddMessage.value = '';
    reAddingInitialDataSuccessful.value = false;
  }, dur);
}

function resetProps () {
  id.value = null;
  createdAt.value = null;
  selectedColorId.value = 20;
  activityTypeInitialData.value = {};
  activityTypeModalMode.value = 'edit';
}

function clearFeedbackTimeout () {
  setTimeout(() => {
    feedback.value = null;
  }, 1000);
}


async function openModal (itemId) {
  idOfItemToDelete.value = itemId;
  const confirmed = await deleteConfirmStore.ask({
    title: 'Delete Activity Type',
    message: 'Are you sure you want to delete this activity type?',
    warning: 'This action cannot be undone.'
  });

  if (!confirmed) {
    idOfItemToDelete.value = null;
    return;
  }

  await deleteItem();
  idOfItemToDelete.value = null;
}


function uncheckList () {
  currentActivityType.value = -1;
}

function setCurrentActivityTypeObj (val = null) {
  if (val === null) {
    const obj = activityTypesStore.activityTypes;
    const currentId = activityTypesStore.currentActivityType;
    const item = obj?.find(x => x.id === currentId);
    activityTypesStore.setCurrentActivityTypeObj(item);
  }
  else {
    activityTypesStore.setCurrentActivityTypeObj(val);
  }
}

async function reAddInitialData () {
  try {
    await GeoMoverDB.reAddStoreData('activityTypes');
    reAddMessage.value = 'Added initial data.';
    clearReAddMsgField(2500);
    reAddingInitialDataSuccessful.value = true;
  }
  catch (e) {
    reAddMessage.value = '<p class="mb-2">Could not add (all of the) initial data. Probably there is an activity with the same name already existing in your list of activities. Names have to be unique.</p><p class="text-xs">(This message will disappear after 10 seconds.)</p>';
    clearReAddMsgField(10000);
  }
  finally {
    getAllActivityTypes();
  }
}

// Helper to get hex color for display from item's colorId
function getHexForItem (item) {
  return colorPalettesStore.getHexById(item.colorId) || '#EC407A';
}

// --- WATCHERS ---
watch(activeMenu, (newVal, oldVal) => {
  if (oldVal === 'activity-types' && newVal !== 'activity-types') {
    closeAllModes();
  }
});

watch(mainNavStatus, (isOpen) => {
  if (!isOpen) {
    closeAllModes();
  }
});

watch(dbReady, () => {
  getAllActivityTypes();
});

// --- LIFECYCLE ---
onMounted(() => {
  getAllActivityTypes();
  colorPalettesStore.loadPalettes();
  iconCatalogStore.loadIconCatalog();
});

onBeforeUnmount(() => {
  closeAllModes();
});
</script>

<style scoped>
/** DO NOT REMOVE scoped OR ELSE THE LAYOUT IN MANY PLACES WILL BE MESSED UP!!!! */

/* Component-specific styles only - common list styles are in global CSS */

.item-changed {
  transition: background-color 300ms, color 300ms, border-color 300ms;
  background-color: color-mix(in srgb, var(--color-pink-700) 40%, transparent);
}
</style>
